import React from 'react';
import { compose, withProps } from 'recompose';
import PageModel from '@mm/state/model/PageModel';
import WidgetModel from '@mm/state/model/WidgetModel';
import OptinQuickQuestion from './OptinQuickQuestion/OptinQuickQuestion';
import OptinImage from './OptinImage/OptinImage';
import OptinGallery from './OptinGallery/OptinGallery';
import { OptinGalleryTextFields } from './OptinGalleryTextFields/OptinGalleryTextFields';

import './OptinPageBuilder.scss';

type PropsFromWithProps = {
  qqWidget: WidgetModel | undefined;
  imageWidget: WidgetModel | undefined;
  galleryWidget: WidgetModel | undefined;
};

type InnerProps = PropsFromWithProps;

type OuterProps = {
  optinPage: PageModel;
  onRemoteWidgetUpdated: () => void;
  onImageTitleUpdated?: (value: string) => void;
  showImageFields?: boolean;
};

type supportedWidgetTypes = 'gallery' | 'quick_question' | 'image';

type Props = InnerProps & OuterProps;

const GetAdditionalPropsByTypes = (
  widgets: WidgetModel[],
  types: supportedWidgetTypes[],
): (WidgetModel | undefined)[] => {
  return types.reduce(
    (
      accumulator: (WidgetModel | undefined)[],
      currentValue: supportedWidgetTypes,
    ) => {
      return [
        ...accumulator,
        widgets && widgets.find(w => w.type === currentValue),
      ];
    },
    [],
  );
};

const OptinPageBuilder = ({
  qqWidget,
  imageWidget,
  galleryWidget,
  onRemoteWidgetUpdated,
  onImageTitleUpdated,
  showImageFields,
}: Props) => (
  <div>
    {galleryWidget && (
      <div className="optin-page-builder__gallery-container">
        <div className="optin-page-builder__gallery-widget-image">
          <OptinGallery
            widget={galleryWidget}
            onRemoteWidgetUpdated={onRemoteWidgetUpdated}
          />
        </div>
        {showImageFields && (
          <div className="optin-page-builder__gallery-widget-text-fields">
            <OptinGalleryTextFields
              widget={galleryWidget}
              onImageTitleUpdated={onImageTitleUpdated}
              onRemoteWidgetUpdated={onRemoteWidgetUpdated}
            />
          </div>
        )}
      </div>
    )}
    {imageWidget && (
      <div className="optin-page-builder__image-widget-container">
        <OptinImage
          imageWidget={imageWidget}
          onRemoteWidgetUpdated={onRemoteWidgetUpdated}
        />
      </div>
    )}
    {qqWidget && (
      <div className="optin-page-builder__qq-widget-container">
        <OptinQuickQuestion
          qqWidget={qqWidget}
          onRemoteWidgetUpdated={onRemoteWidgetUpdated}
        />
      </div>
    )}
  </div>
);

const additionalProps = ({
  optinPage,
}: Props): PropsFromWithProps | undefined => {
  if (!optinPage) return;
  const { widgets } = optinPage;
  const [galleryWidget, qqWidget, imageWidget] = GetAdditionalPropsByTypes(
    widgets as WidgetModel[],
    ['gallery', 'quick_question', 'image'],
  );

  return { qqWidget, imageWidget, galleryWidget };
};

const enhance = compose<InnerProps, OuterProps>(withProps(additionalProps));

export default enhance(OptinPageBuilder);
